import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL;
import {
  Lock, TrendingUp, Users, CheckCircle2, XCircle, RefreshCw,
  Download, FolderOpen, Mail, Type, Save, FileDown,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Admin — area riservata di Roberta (password ADMIN_PASSWORD in backend/.env).
 * Tab: Panoramica (analytics), Check-Up (archivio con cartelle + download report),
 * Newsletter (iscritti + export CSV), Testi del sito (mini-CMS).
 */
export default function AdminAnalytics() {
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("panoramica");

  const pwd = sessionStorage.getItem("rc_admin_pwd") || password;

  const login = async (candidate) => {
    setLoading(true); setErr("");
    try {
      await api.post("/admin/stats", { password: candidate });
      setAuthed(true);
      sessionStorage.setItem("rc_admin_pwd", candidate);
    } catch (e) {
      setErr(e.response?.status === 401 ? "Password non valida" : "Errore di caricamento");
      setAuthed(false);
      sessionStorage.removeItem("rc_admin_pwd");
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const saved = sessionStorage.getItem("rc_admin_pwd");
    if (saved) login(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!authed) {
    return (
      <section className="section-pad">
        <div className="max-w-md mx-auto px-6">
          <div className="flex items-center gap-3 mb-6">
            <Lock size={20} className="text-[#A8674F]"/>
            <p className="eyebrow">Area riservata</p>
          </div>
          <h1 className="font-display text-4xl text-[#2A3229] leading-tight mb-8">
            La tua dashboard
          </h1>
          <form onSubmit={(e) => { e.preventDefault(); if (password) login(password); }} className="bg-white border border-[#323C36]/12 p-8 space-y-5">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#5A6058] mb-2 block">Password</label>
              <input data-testid="admin-pwd" type="password" className="field-input"
                value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />
            </div>
            {err && <p className="text-sm text-[#A8674F]" data-testid="admin-err">{err}</p>}
            <button data-testid="admin-login" type="submit" disabled={loading} className="btn-primary w-full">
              {loading ? "Verifica…" : "Accedi"}
            </button>
          </form>
        </div>
      </section>
    );
  }

  const TABS = [
    { id: "panoramica", label: "Panoramica", icon: <TrendingUp size={14}/> },
    { id: "checkup", label: "Check-Up", icon: <FolderOpen size={14}/> },
    { id: "newsletter", label: "Newsletter", icon: <Mail size={14}/> },
    { id: "testi", label: "Testi del sito", icon: <Type size={14}/> },
  ];

  return (
    <section className="section-pad">
      <div className="max-w-6xl mx-auto px-6">
        <div className="mb-8">
          <p className="eyebrow mb-2">Area riservata</p>
          <h1 className="font-display text-4xl text-[#2A3229] leading-tight">La tua dashboard</h1>
        </div>

        <div className="flex flex-wrap gap-2 mb-10" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-[0.16em] font-semibold border transition-all duration-300 ${
                tab === t.id
                  ? "bg-[#2A3229] text-[#F6F3EF] border-[#2A3229]"
                  : "bg-white text-[#5A6058] border-[#323C36]/15 hover:border-[#323C36]"
              }`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {tab === "panoramica" && <PanoramicaTab pwd={pwd} />}
        {tab === "checkup" && <CheckupTab pwd={pwd} />}
        {tab === "newsletter" && <NewsletterTab pwd={pwd} />}
        {tab === "testi" && <TestiTab pwd={pwd} />}
      </div>
    </section>
  );
}

/* ---------------- PANORAMICA ---------------- */
function PanoramicaTab({ pwd }) {
  const [stats, setStats] = useState(null);
  const load = async () => {
    try { const r = await api.post("/admin/stats", { password: pwd }); setStats(r.data); }
    catch { toast.error("Errore di caricamento"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  if (!stats) return <p className="text-sm text-[#5A6058]">Caricamento…</p>;
  const s = stats;
  return (
    <div>
      <div className="flex justify-end mb-6">
        <button onClick={load} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-[#323C36] hover:text-[#A8674F]" data-testid="admin-refresh">
          <RefreshCw size={14}/> Aggiorna
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <KPI icon={<Users size={18}/>} label="Inizi (totale)" value={s.starts.total} sub={`${s.starts.last_7d} negli ultimi 7gg`}/>
        <KPI icon={<CheckCircle2 size={18}/>} label="Completati" value={s.completed.total} sub={`${s.completed.last_7d} negli ultimi 7gg`} accent/>
        <KPI icon={<TrendingUp size={18}/>} label="Tasso di completamento" value={`${s.completion_rate}%`} sub="rapporto submit / start"/>
        <KPI icon={<XCircle size={18}/>} label="Abbandonati" value={s.abandoned_count} sub="iniziati e non completati"/>
      </div>
      <div className="bg-white border border-[#323C36]/12 p-6 sm:p-8 mb-8">
        <p className="eyebrow mb-4">Ultimi Check-Up completati</p>
        {s.recent_completions.length === 0 ? (
          <p className="text-sm text-[#5A6058]">Nessun completamento ancora.</p>
        ) : (
          <div className="divide-y divide-[#323C36]/10">
            {s.recent_completions.map((r, i) => (
              <div key={i} className="py-3 flex flex-wrap items-center gap-4 justify-between text-sm">
                <div className="min-w-0">
                  <p className="font-semibold text-[#2A3229] truncate">{r.client?.name || "—"} · <span className="text-[#A8674F]">{r.client?.company || "—"}</span></p>
                  <p className="text-[#5A6058] text-xs truncate">{r.client?.email} · {r.client?.phone}</p>
                </div>
                <p className="text-xs text-[#5A6058] shrink-0">{formatDate(r.created_at)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="bg-white border border-[#323C36]/12 p-6 sm:p-8">
        <p className="eyebrow mb-4">Inizi non completati (ultimi 20)</p>
        {s.recent_abandoned.length === 0 ? (
          <p className="text-sm text-[#5A6058]">Nessun abbandono registrato.</p>
        ) : (
          <div className="divide-y divide-[#323C36]/10">
            {s.recent_abandoned.map((r, i) => (
              <div key={i} className="py-3 flex flex-wrap items-center gap-4 justify-between text-sm">
                <div className="min-w-0">
                  <p className="font-semibold text-[#2A3229] truncate">{r.company}</p>
                  {r.email && <p className="text-[#5A6058] text-xs truncate">{r.email}</p>}
                </div>
                <p className="text-xs text-[#5A6058] shrink-0">{formatDate(r.created_at)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- CHECK-UP (archivio + cartelle + download) ---------------- */
function CheckupTab({ pwd }) {
  const [data, setData] = useState(null);
  const [activeFolder, setActiveFolder] = useState("Tutti");
  const [moving, setMoving] = useState("");

  const load = async () => {
    try { const r = await api.post("/admin/results", { password: pwd }); setData(r.data); }
    catch { toast.error("Errore di caricamento"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (!data) return <p className="text-sm text-[#5A6058]">Caricamento…</p>;

  const folders = [...new Set(["Nuovi", "Archiviati", ...data.folders])];
  const results = activeFolder === "Tutti" ? data.results : data.results.filter((r) => r.folder === activeFolder);

  const move = async (r, folder) => {
    let target = folder;
    if (folder === "__new__") {
      target = window.prompt("Nome della nuova cartella:");
      if (!target || !target.trim()) return;
      target = target.trim();
    }
    setMoving(r.id);
    try {
      await api.post("/admin/results/move", { password: pwd, id: r.id, folder: target });
      setData((d) => ({
        ...d,
        results: d.results.map((x) => (x.id === r.id ? { ...x, folder: target } : x)),
        folders: [...new Set([...d.folders, target])],
      }));
      toast.success(`Spostato in "${target}"`);
    } catch { toast.error("Spostamento non riuscito"); }
    finally { setMoving(""); }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6" data-testid="folder-list">
        {["Tutti", ...folders].map((f) => (
          <button
            key={f}
            data-testid={`folder-${f}`}
            onClick={() => setActiveFolder(f)}
            className={`px-4 py-2 text-xs uppercase tracking-[0.14em] font-semibold border transition-all duration-300 ${
              activeFolder === f ? "bg-[#A8674F] text-[#F6F3EF] border-[#A8674F]" : "bg-white text-[#5A6058] border-[#323C36]/15 hover:border-[#323C36]"
            }`}
          >
            {f}{f !== "Tutti" && ` (${data.results.filter((r) => r.folder === f).length})`}
          </button>
        ))}
      </div>

      {results.length === 0 ? (
        <p className="text-sm text-[#5A6058]">Nessun Check-Up in questa vista.</p>
      ) : (
        <div className="bg-white border border-[#323C36]/12 divide-y divide-[#323C36]/10">
          {results.map((r) => (
            <div key={r.id} data-testid={`result-${r.id}`} className="p-5 flex flex-wrap items-center gap-4 justify-between">
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-[#2A3229] truncate">
                  {r.client?.name || "—"} · <span className="text-[#A8674F]">{r.client?.company || "—"}</span>
                </p>
                <p className="text-[#5A6058] text-xs truncate mt-1">
                  {r.client?.email} · {r.client?.phone} · {formatDate(r.created_at)}
                  {typeof r.overall_percent === "number" && <> · Punteggio <strong className="text-[#323C36]">{r.overall_percent}%</strong></>}
                  {" · "}<span className={`font-semibold ${r.mode === "quick" ? "text-[#A8674F]" : "text-[#323C36]"}`}>{r.mode === "quick" ? "Rapido" : "Completo"}</span>
                </p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <select
                  data-testid={`move-${r.id}`}
                  value={r.folder}
                  disabled={moving === r.id}
                  onChange={(e) => move(r, e.target.value)}
                  className="text-xs border border-[#323C36]/20 bg-white px-3 py-2 text-[#2A3229] outline-none focus:border-[#323C36]"
                >
                  {folders.map((f) => <option key={f} value={f}>{f}</option>)}
                  <option value="__new__">+ Nuova cartella…</option>
                </select>
                {r.has_report ? (
                  <a
                    data-testid={`download-${r.id}`}
                    href={`${BACKEND_ORIGIN}${r.report_url}`}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.14em] font-semibold px-4 py-2 bg-[#2A3229] text-[#F6F3EF] hover:bg-[#323C36] transition-colors"
                  >
                    <Download size={13}/> Report .docx
                  </a>
                ) : (
                  <span className="text-xs text-[#5A6058]/60 inline-flex items-center gap-2"><FileDown size={13}/> Report non disponibile</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- NEWSLETTER ---------------- */
function NewsletterTab({ pwd }) {
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    try { const r = await api.post("/admin/subscribers", { password: pwd }); setData(r.data); }
    catch { toast.error("Errore di caricamento"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await api.post("/admin/subscribers/export", { password: pwd }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "iscritti-newsletter.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export non riuscito"); }
    finally { setExporting(false); }
  };

  if (!data) return <p className="text-sm text-[#5A6058]">Caricamento…</p>;

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <p className="text-sm text-[#5A6058]"><strong className="text-[#2A3229] text-lg font-display">{data.total}</strong> iscritti alla newsletter</p>
        <button data-testid="export-csv" onClick={exportCsv} disabled={exporting || data.total === 0} className="btn-primary !py-2.5 text-xs disabled:opacity-50">
          <Download size={13}/> {exporting ? "Preparazione…" : "Esporta CSV"}
        </button>
      </div>
      {data.subscribers.length === 0 ? (
        <p className="text-sm text-[#5A6058]">Nessun iscritto ancora.</p>
      ) : (
        <div className="bg-white border border-[#323C36]/12 divide-y divide-[#323C36]/10">
          {data.subscribers.map((s, i) => (
            <div key={i} className="px-5 py-3.5 flex items-center justify-between gap-4 text-sm">
              <p className="text-[#2A3229] font-medium truncate">{s.email}</p>
              <p className="text-xs text-[#5A6058] shrink-0">{formatDate(s.created_at)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- TESTI DEL SITO ---------------- */
const TEXT_FIELDS = [
  { key: "hero_subtitle", label: "Home — Sottotitolo principale", placeholder: "Per trasformare il caos gestionale in crescita scalabile." },
  { key: "slogan_text", label: "Home — Slogan (banda scura)", placeholder: "Più ordine, più margini, zero sprechi." },
  { key: "newsletter_title", label: "Newsletter — Titolo", placeholder: "Ogni settimana, una cosa concreta su cui lavorare." },
  { key: "newsletter_text", label: "Newsletter — Testo promessa", placeholder: "Niente teoria e niente spam: un'azione pratica per migliorare la tua impresa, da applicare subito. È la mia promessa." },
  { key: "footer_claim", label: "Footer — Descrizione sotto il logo", placeholder: "Consulenza per le aziende: oltre 10 anni di esperienza nel management…" },
  { key: "chisono_intro", label: "Chi Sono — Paragrafo introduttivo", placeholder: "Sono Roberta Castagna, Consulente d'Impresa e Brand Strategist…" },
];

function TestiTab({ pwd }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/texts").then((r) => setValues(r.data?.texts || {})).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/texts", { password: pwd, texts: values });
      toast.success("Testi salvati. Ricarica il sito per vederli online.");
    } catch { toast.error("Salvataggio non riuscito"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-[#323C36]/12 p-6 sm:p-8">
      <p className="text-sm text-[#5A6058] mb-6 max-w-2xl">
        Modifica i testi principali del sito senza passare dalla chat. Lascia il campo vuoto per tornare al testo originale. Dopo il salvataggio, ricarica la pagina del sito per vedere la modifica.
      </p>
      <div className="space-y-6">
        {TEXT_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-xs uppercase tracking-widest text-[#5A6058] mb-2 block">{f.label}</label>
            <textarea
              data-testid={`text-${f.key}`}
              rows={2}
              className="field-input resize-y"
              placeholder={f.placeholder}
              value={values[f.key] || ""}
              onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
            />
          </div>
        ))}
      </div>
      <button data-testid="save-texts" onClick={save} disabled={saving} className="btn-primary mt-8">
        <Save size={14}/> {saving ? "Salvataggio…" : "Salva le modifiche"}
      </button>
    </div>
  );
}

/* ---------------- shared ---------------- */
function KPI({ icon, label, value, sub, accent }) {
  return (
    <div className={`p-6 border ${accent ? 'bg-[#2A3229] text-[#F6F3EF] border-[#2A3229]' : 'bg-white border-[#323C36]/12'}`}>
      <div className={`inline-flex items-center gap-2 mb-3 ${accent ? 'text-[#FAC075]' : 'text-[#A8674F]'}`}>
        {icon}
        <span className="text-[10px] uppercase tracking-[0.22em] font-semibold">{label}</span>
      </div>
      <p className={`font-display text-4xl leading-none tabular-nums ${accent ? '' : 'text-[#2A3229]'}`}>{value}</p>
      <p className={`text-xs mt-3 ${accent ? 'text-[#F6F3EF]/60' : 'text-[#5A6058]'}`}>{sub}</p>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}
